import { Draft, PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { getUserId } from "../../../authentication/store/auth.selector";
import { RootState } from "../../../store-types";
import { IObservationPhotoUploadDto } from "../shared/dtos/observation-photo-upload.dto";
import { IObservationEditingState } from "../shared/interfaces/types";
import { Observation } from "../shared/models/observation";
import { IObservationPhoto } from "../shared/models/observation-photo";
import { observationsService } from "../shared/services/observations.service";

import { OBSERVATION_EDITING_MODULE_NAME } from "./constants";
import { getObservationEditingState } from "./observation-editing.selector";

const initialState: IObservationEditingState = {
  isLoading: false,
  existingPhotos: [],
  photoFiles: [],
  observation: new Observation(),
};

type SaveObservationPayload = {
  data: Observation;
  shouldUpdateStore?: boolean;
};

type UploadPhotoFilesPayload = {
  observationId?: string;
  photoFiles?: File[];
  shouldUpdateStore?: boolean;
};

const isCurrentEditingObservation = (state: RootState, observationId: string): boolean =>
  getObservationEditingState(state).observation.id.toLowerCase() ===
  observationId.toLowerCase();

export const observationEditingSlice = createSlice({
  name: OBSERVATION_EDITING_MODULE_NAME,
  initialState,
  reducers: {
    setObservationData(state, action: PayloadAction<Observation>): void {
      state.observation = action.payload;
    },
    setExistingPhotosAction(state, action: PayloadAction<IObservationPhoto[]>): void {
      state.existingPhotos = action.payload;
    },
    addExistingPhotosAction(state, action: PayloadAction<IObservationPhoto[]>): void {
      state.existingPhotos = [...state.existingPhotos, ...action.payload];
    },
    markPhotoForDeleteAction(state, action: PayloadAction<string>): void {
      state.existingPhotos = state.existingPhotos.map((photo) => {
        if (photo.id !== action.payload) {
          return photo;
        }
        return {
          ...photo,
          toDelete: !photo.toDelete,
        };
      });
    },
    removeExistingPhotoAction(state, action: PayloadAction<string>): void {
      state.existingPhotos = state.existingPhotos.filter(
        (photo) => photo.id !== action.payload
      );
    },
    setPhotoFilesAction(state, action: PayloadAction<File[]>): void {
      state.photoFiles = action.payload;
    },
    addPhotoFilesAction(state, action: PayloadAction<File[]>): void {
      state.photoFiles = [...state.photoFiles, ...action.payload];
    },
    removePhotoFileAction(state, action: PayloadAction<number>): void {
      state.photoFiles = [...state.photoFiles.filter((_, idx) => idx !== action.payload)];
    },
    clearPhotosListsAction(state): void {
      state.existingPhotos = [];
      state.photoFiles = [];
    },
  },
  extraReducers(builder) {
    const enableLoading = (state: Draft<IObservationEditingState>): void => {
      state.isLoading = true;
    };
    const disableLoading = (state: Draft<IObservationEditingState>): void => {
      state.isLoading = false;
    };

    builder
      .addCase(fetchObservationByIdAction.pending, enableLoading)
      .addCase(fetchObservationByIdAction.fulfilled, disableLoading)
      .addCase(fetchObservationByIdAction.rejected, disableLoading)
      .addCase(addObservationAction.pending, enableLoading)
      .addCase(addObservationAction.fulfilled, disableLoading)
      .addCase(addObservationAction.rejected, disableLoading)
      .addCase(updateObservationAction.pending, enableLoading)
      .addCase(updateObservationAction.fulfilled, disableLoading)
      .addCase(updateObservationAction.rejected, disableLoading)
      .addCase(completeObservationReportAction.pending, enableLoading)
      .addCase(completeObservationReportAction.fulfilled, disableLoading)
      .addCase(completeObservationReportAction.rejected, disableLoading)
      .addCase(uploadPhotoFiles.pending, enableLoading)
      .addCase(uploadPhotoFiles.fulfilled, disableLoading)
      .addCase(uploadPhotoFiles.rejected, disableLoading);
  },
});

export const observationEditingReducer = observationEditingSlice.reducer;
export const {
  setObservationData,
  setExistingPhotosAction,
  addExistingPhotosAction,
  markPhotoForDeleteAction,
  removeExistingPhotoAction,
  setPhotoFilesAction,
  addPhotoFilesAction,
  removePhotoFileAction,
  clearPhotosListsAction,
} = observationEditingSlice.actions;

export const fetchObservationByIdAction = createAsyncThunk<Observation, string>(
  `${OBSERVATION_EDITING_MODULE_NAME}`,
  async (observationId, { dispatch, signal }) => {
    const dto = await observationsService.get(observationId, { signal });
    const model = new Observation(dto.id);
    model.updateFromDto(dto);
    dispatch(setObservationData(model));
    dispatch(setExistingPhotosAction(model.observationPhotos));
    return model;
  }
);

export const addObservationAction = createAsyncThunk<
  Observation,
  SaveObservationPayload,
  { state: RootState }
>(
  `${OBSERVATION_EDITING_MODULE_NAME}/addObservation`,
  async ({ data, shouldUpdateStore = true }, { dispatch, getState }) => {
    data.resetCreatedOnClientAt();
    const dto = await observationsService.add(data.asUpsertDto);
    const newModel = new Observation(dto.id);
    newModel.updateFromDto(dto);
    if (shouldUpdateStore && isCurrentEditingObservation(getState(), newModel.id)) {
      dispatch(setObservationData(newModel));
    }
    return newModel;
  }
);

export const updateObservationAction = createAsyncThunk<
  Observation,
  SaveObservationPayload,
  { state: RootState }
>(
  `${OBSERVATION_EDITING_MODULE_NAME}/updateObservation`,
  async ({ data, shouldUpdateStore = true }, { dispatch, getState }) => {
    const dto = await observationsService.update(data.asUpsertDto);
    const newModel = new Observation(dto.id);
    newModel.updateFromDto(dto);
    if (shouldUpdateStore && isCurrentEditingObservation(getState(), newModel.id)) {
      dispatch(setObservationData(newModel));
      const { existingPhotos } = getObservationEditingState(getState());
      existingPhotos.forEach((photo) => {
        if (photo.toDelete) {
          dispatch(deleteExistingPhotoAction(photo.id));
        }
      });
    }
    return newModel;
  }
);

export const completeObservationReportAction = createAsyncThunk<
  Observation | undefined,
  string
>(
  `${OBSERVATION_EDITING_MODULE_NAME}/completeObservationReport`,
  async (observationId, { dispatch, getState }) => {
    const dto = await observationsService.completeReport(observationId);
    const completedObservation = dto ? Observation.fromDto(dto) : undefined;
    completedObservation?.completeReport();

    if (isCurrentEditingObservation(getState() as RootState, observationId)) {
      const currentObservation = getObservationEditingState(
        getState() as RootState
      ).observation;
      const observationForStore =
        completedObservation ?? Observation.fromDto(currentObservation.asDto);
      observationForStore.completeReport();
      dispatch(setObservationData(observationForStore));
    }

    return completedObservation;
  }
);

export const deleteExistingPhotoAction = createAsyncThunk<void, string>(
  `${OBSERVATION_EDITING_MODULE_NAME}/deleteObservationPhoto`,
  async (id, { dispatch }) => {
    dispatch(removeExistingPhotoAction(id));
    return observationsService.deletePhoto(id);
  }
);

export const uploadPhotoFiles = createAsyncThunk<
  IObservationPhotoUploadDto[] | undefined,
  UploadPhotoFilesPayload,
  { state: RootState }
>(
  `${OBSERVATION_EDITING_MODULE_NAME}/uploadObservationPhoto`,
  async (payload, { dispatch, getState }) => {
    const state = getState();
    const editingState = getObservationEditingState(state);
    const userId = getUserId(state);
    const observationId = payload.observationId ?? editingState.observation.id;
    const photoFiles = payload.photoFiles ?? editingState.photoFiles;
    const shouldUpdateStore = payload.shouldUpdateStore ?? true;

    if (!photoFiles.length) {
      return;
    }
    if (!userId) {
      throw new Error("Can't upload observation photos without appUserId");
    }

    const result = await observationsService.uploadPhotos(
      observationId,
      userId,
      photoFiles
    );
    if (shouldUpdateStore && isCurrentEditingObservation(getState(), observationId)) {
      dispatch(addExistingPhotosAction(result));
      dispatch(setPhotoFilesAction([]));
    }
    return result;
  }
);
